import { useCallback, useEffect, useRef } from "react";
import { getDapiRandomId } from "./dapi-cookie";
import eventClient from "./event-client";

declare global {
  interface Window {
    // Add DAPI debug helper
    _DAPI_DEBUG?: {
      dapiRandomId: string | null;
      webContextSessionId: string | null;
      hasEventFired: boolean;
      fireDAPIEvent: () => void;
    };
    cohesion: ((event: string, callback?: (...args: any[]) => void) => void) | undefined;
  }
}

interface DAPIOptions {
  enabled?: boolean;
}

export const useDAPI = (options: DAPIOptions = {}) => {
  const { enabled = true } = options;

  const dapiRandomIdRef = useRef<string | null>(null);
  const dapiEventFiredRef = useRef(false);
  const webContextSessionIdRef = useRef<string | null>(null);

  // Check for existing preconsent ID on hook initialization
  useEffect(() => {
    if (!enabled) return;

    const storedId = getDapiRandomId();
    if (!storedId) {
      console.error('[DAPI Error] Error: No stored ID found in cookie. DAPI event will not be fired.');
      dapiRandomIdRef.current = null;
    } else {
      dapiRandomIdRef.current = storedId;
    }
  }, [enabled]);

  // Set up listener for tagular events to capture webContext.sessionId
  useEffect(() => {
    if (!enabled) return;

    const handleTagularEvent = (event: any) => {
      if (event?.properties?.webContext?.sessionId && !webContextSessionIdRef.current) {
        webContextSessionIdRef.current = event.properties.webContext.sessionId;
      }
    };

    if (window.cohesion) {
      window.cohesion("tagular:eventSucceeded", handleTagularEvent);
    }

    return () => { };
  }, [enabled]);

  const fireDAPIEvent = useCallback(() => {
          if (!enabled || dapiEventFiredRef.current) {
        return;
    }

    if (!dapiRandomIdRef.current) {
      console.error('[DAPI Error] Error: Cannot fire DAPI event - No randomId found in cookie. This indicates the ID was not properly set during pre-consent phase.');
      return;
    }

    const dapiData = {
      dapiRandomId: dapiRandomIdRef.current,
    };

    try {
      eventClient.dapiCorrelation(dapiData);
      dapiEventFiredRef.current = true;
    } catch (error) {
      console.error("[DAPI Error] Error firing DAPI event:", error);
    }
  }, [enabled]);

  // Expose debug helper to window (only in development)
  useEffect(() => {
    if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
      window._DAPI_DEBUG = {
        dapiRandomId: dapiRandomIdRef.current,
        webContextSessionId: webContextSessionIdRef.current,
        hasEventFired: dapiEventFiredRef.current,
        fireDAPIEvent,
      };
    }
  });

  return {
    fireDAPIEvent,
    dapiRandomId: dapiRandomIdRef.current,
    webContextSessionId: webContextSessionIdRef.current,
  };
};
